import { DialogContent, Grid, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLightningDetailsQuery } from 'features/mapping/components/layers/LightningLayer/useLightningDetailsQuery';
import {
  formatDate,
  getTimeAgo,
  SHORT_MONTH_DAY_12HOUR_FORMAT,
} from 'shared/dates';
import { getLatLngLocationName } from 'shared/utils';
import { GeoEventMetadataText } from 'features/geoEvents/components/GeoEventMetadataRows';
import { LightningPoi } from './PoiDialog.types';
import { EMPTY_DATA_VALUE } from '../../../../../constants';

const ValueSkeleton = (): JSX.Element => (
  <Skeleton variant="text" width="60%" />
);

export const LightningDialogContent = (props: LightningPoi): JSX.Element => {
  const { t } = useTranslation();
  const { id, flashTime, latitude, longitude } = props;

  const { lightningDetails, isLoading } = useLightningDetailsQuery({ id });

  const {
    flashCurrentKa,
    flashPolarity,
    flashType,
    flashDurationNs,
    sensorCount,
  } = lightningDetails ?? {};

  let flashTypeFormatted: string;
  switch (flashType) {
    case 0:
      flashTypeFormatted = t('map.poi.lightning.flashType.cloudToGround');
      break;
    case 1:
      flashTypeFormatted = t('map.poi.lightning.flashType.intraCloud');
      break;
    default:
      flashTypeFormatted = EMPTY_DATA_VALUE;
  }

  const flashTimeRelative = flashTime && getTimeAgo(flashTime);
  const flashTimeFormatted =
    flashTime && formatDate(flashTime, SHORT_MONTH_DAY_12HOUR_FORMAT);
  const flashDurationSeconds = flashDurationNs
    ? flashDurationNs / 1_000_000_000
    : null;
  const flashCurrentFormatted = flashCurrentKa
    ? t('map.poi.lightning.flashCurrent.value', {
        value: flashCurrentKa.toLocaleString(),
      })
    : EMPTY_DATA_VALUE;
  const flashDurationFormatted = flashDurationSeconds
    ? t('map.poi.lightning.flashDuration.value', {
        value: flashDurationSeconds.toLocaleString(),
      })
    : EMPTY_DATA_VALUE;
  const sensorCountFormatted =
    sensorCount?.toLocaleString() ?? EMPTY_DATA_VALUE;
  const coordinatesFormatted = getLatLngLocationName(latitude, longitude);

  return (
    <DialogContent sx={{ padding: 2, maxWidth: 450 }}>
      <Typography variant="body2" sx={{ marginBottom: 1 }}>
        <b>{t('map.poi.lightning.title')}</b>
      </Typography>

      <Grid
        container
        sx={{
          width: '100%',
          alignItems: 'baseline',
        }}
      >
        <Grid size={3}>
          <Typography variant="h4" color="secondary">
            {t('map.poi.lightning.flashType.label')}
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography variant="body2">
            {isLoading ? <ValueSkeleton /> : flashTypeFormatted}
          </Typography>
        </Grid>

        <Grid size={3}>
          <Typography variant="h4" color="secondary">
            {t('map.poi.lightning.flashCurrent.label')}
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography variant="body2">
            {isLoading ? <ValueSkeleton /> : flashCurrentFormatted}
          </Typography>
        </Grid>

        <Grid size={3}>
          <Typography variant="h4" color="secondary">
            {t('map.poi.lightning.flashPolarity.label')}
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography variant="body2">
            {isLoading ? (
              <ValueSkeleton />
            ) : (
              (flashPolarity ?? EMPTY_DATA_VALUE)
            )}
          </Typography>
        </Grid>

        <Grid size={3}>
          <Typography variant="h4" color="secondary">
            {t('map.poi.lightning.flashDuration.label')}
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography variant="body2">
            {isLoading ? <ValueSkeleton /> : flashDurationFormatted}
          </Typography>
        </Grid>

        <Grid size={3}>
          <Typography variant="h4" color="secondary">
            {t('map.poi.lightning.sensorCount.label')}
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography variant="body2">
            {isLoading ? <ValueSkeleton /> : sensorCountFormatted}
          </Typography>
        </Grid>

        <Grid size={3}>
          <Typography variant="h4" color="secondary">
            {t('map.poi.lightning.coordinates.label')}
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography variant="body2">
            {isLoading ? <ValueSkeleton /> : coordinatesFormatted}
          </Typography>
        </Grid>
      </Grid>

      <GeoEventMetadataText sx={{ marginTop: 1 }}>
        {t('map.poi.lightning.recorded', {
          absolute: flashTimeFormatted,
          relative: flashTimeRelative,
        })}
      </GeoEventMetadataText>
    </DialogContent>
  );
};
